import * as Sentry from '@sentry/angular';
import { AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Router, NavigationEnd } from '@angular/router';

import { environment } from '@env';
import { awaitHandler } from '@utils/awaitHandler';

import { first } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as compareVersions from 'compare-versions';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  // true while a downloaded update is waiting for the user to reload into it
  public readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

  private readonly frequency: number = 1000 * 60 * 10; // 10 minutes
  private isStarted = false;
  private intervalListener: any;
  private readyVersion: string;
  private dismissedVersion: string;

  constructor(
    private swUpdate: SwUpdate,
    private storage: Storage,
    private logger: NGXLogger,
    private ngZone: NgZone,
    private router: Router,
    private translate: TranslateService,
    private alertController: AlertController
  ) { }

  // Resolves without waiting for an update to download: the guard on `pages` awaits this,
  // and blocking on checkForUpdate() rendered a blank screen for the whole download.
  public startListening(): Promise<boolean> {
    if (this.isStarted || !('serviceWorker' in navigator && navigator?.serviceWorker?.controller)) {
      return Promise.resolve(true);
    }

    if (this.swUpdate.isEnabled) {
      // we can actually monitor for changes

      this.isStarted = true;
      this.swUpdate.versionUpdates.subscribe((evt) => {
        this.logger.debug(`versionUpdate sent event ${evt.type}`);
        switch (evt.type) {
          case 'VERSION_DETECTED':
            this.logger.debug("version-check: New version detected: waiting for READY");
            break;
          case 'VERSION_READY':
            this.logger.debug("version-check: New version ready: offering reload");
            if (evt.latestVersion.hash !== this.dismissedVersion) {
              this.readyVersion = evt.latestVersion.hash;
              this.ngZone.run(() => this.updateAvailable$.next(true));
            }
            break;
          case 'VERSION_INSTALLATION_FAILED':
            this.logger.debug(`version-check: There was an error - ${evt.error}`);
            break;
          case 'NO_NEW_VERSION_DETECTED':
            // this.logger.debug("version-check: No new version");
            break;
        }
      });

      this.swUpdate.unrecoverable.subscribe(async (event) => {
        const message = `version-check: An error occurred that we cannot recover from: ${event.reason}`;
        this.logger.debug(message);

        if (Sentry.isInitialized()) {
          Sentry.captureMessage(message);
        }

        this.notifyUserOfAppErrorAndReload();
      });

      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          // Run outside Angular zone to avoid triggering change detection
          this.ngZone.runOutsideAngular(() => {
            // this.logger.debug(`version-check: Checking for update while routing`);
            this.swUpdate.checkForUpdate();
          });
        });

      this.intervalListener = setInterval(() => {
        this.checkVersion();
      }, this.frequency);
    }

    return this.checkVersion();
  }

  public dismissUpdate(): void {
    this.dismissedVersion = this.readyVersion;
    this.updateAvailable$.next(false);
  }

  // activateUpdate() moves this tab onto the downloaded version; reload even if it fails,
  // since the next navigation request is served the new version regardless.
  public async applyUpdate(): Promise<void> {
    const [, err] = await awaitHandler(this.swUpdate.activateUpdate());
    if (err) {
      this.logger.warn(`version-check: activateUpdate failed: ${err}`);
    }
    this.reloadPage();
  }

  private reloadPage(): void {
    document.location.reload();
  }

  private async checkVersion(): Promise<boolean> {
    if (this.swUpdate.isEnabled) {
      this.logger.debug("version-check: checking for new web app version");
      // not awaited: it resolves only once a new version has fully downloaded
      this.swUpdate.checkForUpdate().catch((err) => this.logger.debug(`version-check: checkForUpdate failed: ${err}`));

      await this.checkMajorVersion();
    } else {
      this.logger.warn('swUpdate is not enabled');
    }

    return true;
  }

  private async checkMajorVersion() {
    const isVersionDifferent = await this.isVersionDifferent();

    if (isVersionDifferent) {
      await this.storage.clear();
    } else {
      this.storage.set('version', environment.appVersion);
    }
  }

  private async isVersionDifferent(): Promise<boolean> {
    const getVersion = (versionValue) => versionValue && <string>first(versionValue.match(/\d+(\.\d+)/));
    const currentVersion = getVersion(environment.appVersion);
    const [versionFromStorage] = await awaitHandler(this.storage.get('version'));
    const lastCachedVersion = getVersion(versionFromStorage);

    return lastCachedVersion && compareVersions(currentVersion, lastCachedVersion) > 0;
  }

  private async notifyUserOfAppErrorAndReload() {
    const alertElement: HTMLIonAlertElement = await this.alertController.create({
      header: this.translate.instant('SHARED.Whoops'),
      message: this.translate.instant('SHARED.Version_Check_Error'),
      cssClass: 'custom-alert',
      buttons: [{ text: this.translate.instant('SHARED.OK') }]
    });

    await alertElement.present();
    await alertElement.onDidDismiss();
    this.reloadPage();
  }
}
